import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { PageHeader } from "@shared/components/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { AlertTriangle, Calendar, Clock, Info, Printer } from "lucide-react";
import { useState } from "react";
import { usePrintSummary } from "../../hooks";
import { PrintingTable } from "./PrintingTable";

export function PrintingTab() {
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const { data: summary } = usePrintSummary();

  const handleMetricClick = (metric: string) => {
    setSelectedMetric((prev) => (prev === metric ? null : metric));
  };

  // KPI Cards configuration using QuickStatsCard
  const printingKpiCards = [
    {
      title: "Pending Print Start",
      count: summary?.pendingPrintStart || 0,
      trendSummary: "Print jobs waiting to begin processing",
      trendDirection: "down" as const,
      icon: <Calendar className="h-8 w-8 text-yellow-500" />,
      isSelected: selectedMetric === "pendingPrintStart",
      onClick: () => handleMetricClick("pendingPrintStart"),
    },
    {
      title: "Printing In Progress",
      count: summary?.printingInProgress || 0,
      trendSummary: "Print jobs currently being processed",
      trendDirection: "up" as const,
      icon: <Printer className="h-8 w-8 text-blue-500" />,
      isSelected: selectedMetric === "printingInProgress",
      onClick: () => handleMetricClick("printingInProgress"),
    },
    {
      title: "Printed Today",
      count: summary?.printedToday || 0,
      trendSummary: "Successfully completed print jobs today",
      trendDirection: "up" as const,
      icon: <Printer className="h-8 w-8 text-green-500" />,
      isSelected: selectedMetric === "printedToday",
      onClick: () => handleMetricClick("printedToday"),
    },
    {
      title: "Avg Processing",
      count: summary?.averageProcessingTime || "0 min",
      trendSummary: "Average time to complete print jobs",
      trendDirection: "down" as const,
      icon: <Clock className="h-8 w-8 text-purple-500" />,
      isSelected: selectedMetric === "averageProcessTime",
      onClick: () => handleMetricClick("averageProcessTime"),
    },
    {
      title: "Delayed Print Alerts",
      count: summary?.delayedPrintAlerts || 0,
      trendSummary: "Print jobs behind schedule requiring attention",
      trendDirection: "down" as const,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
      isSelected: selectedMetric === "delayedPrintAlerts",
      onClick: () => handleMetricClick("delayedPrintAlerts"),
    },
  ];

  return (
    <div className="space-y-6 p-1">
      <PageHeader
        title="Printing"
        subtitle="Manage and track print jobs for billing cycles"
      />
      {/* KPI Summary Cards - Using loop with QuickStatsCard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {printingKpiCards.map((card, index) => (
          <div
            key={index}
            className={`cursor-pointer transition-all ${
              card.isSelected ? "ring-2 ring-primary" : ""
            }`}
            onClick={card.onClick}
          >
            <QuickStatsCard
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          </div>
        ))}
      </div>

      <Alert variant="default" className="bg-muted border border-input">
        <Info className="h-4 w-4" />
        <AlertTitle>Tip</AlertTitle>
        <AlertDescription className="text-xs">
          Prioritize printing cycles with the highest number of lapsed days to
          minimize delays. Click a cycle row to view consumer-level print
          details.
        </AlertDescription>
      </Alert>

      <PrintingTable />
    </div>
  );
}
